import { MouseEvent } from "react";

import { checkHasResponse } from "@smart/bridge-types-basic";
import { Loading } from "@smart/itops-sb-design-system-dom";
import { isNotNullOrUndefined } from "@smart/itops-utils-basic";

import {
  Section,
  SectionButtons,
  PageWrapper,
  SectionForm,
  Page,
} from "./section-components";
import { ErrorSummary } from "./shared-components/error-summary";
import { PreviewFormFooter } from "./shared-components/preview-form-footer";
import {
  SummaryField,
  SummaryGroup,
  SummaryGroupFieldSet,
  SummarySection,
} from "./summary-components";
import { renderOrderedItems, requiredMessage } from "../hooks";
import {
  AutoFillStatus,
  FieldItem,
  OrderedGroup,
  OrderedSection,
  SectionItem,
  summaryTitle,
  TeamItem,
} from "../types";

type IndexedFieldItem = FieldItem & { groupIndex?: number };

export type IntakeSummaryProps = {
  team: TeamItem;
  visibleSections: OrderedSection[];
  responses: Record<string, any>;
  isLoadingDBResponses: boolean;
  sidebar: boolean;
  setSidebar: (sidebar: boolean) => void;
  onSelect?: (section: SectionItem) => void;
  onSubmitButton?: (e: MouseEvent<HTMLButtonElement>) => void;
  onSubmit?: () => void;
  onReset?: () => void;
  submitError?: string;
  isForm?: boolean;
  hasPayment: boolean;
  previewErrorToggle?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  autoFillingStatus?: AutoFillStatus;
  isPreview?: boolean;
};

const noop = () => {};

export const IntakeSummary = ({
  team,
  visibleSections,
  responses,
  isLoadingDBResponses,
  sidebar,
  setSidebar,
  onSelect,
  onSubmitButton,
  onSubmit,
  onReset,
  hasPayment,
  submitError,
  isForm = true,
  previewErrorToggle,
  autoFillingStatus,
  isPreview,
}: IntakeSummaryProps) => {
  const missingRequired = visibleSections
    .map(({ orderedItems }) =>
      orderedItems.map(
        (item) =>
          (item.type === "group"
            ? item.groupFieldSets
                .map((fieldSet) =>
                  fieldSet.visibleFields.map((fs) => ({
                    ...fs,
                    groupIndex: fieldSet.index,
                  })),
                )
                .flat()
            : [item.field]) as IndexedFieldItem[],
      ),
    )
    .flat(2)
    .filter(
      (field) =>
        field.mandatory &&
        !checkHasResponse(
          field.type,
          field.groupUri
            ? responses[field.uri]?.[field.groupIndex || 0]
            : responses[field.uri],
        ),
    );

  const onGotoError = (sectionUri: string) => {
    const section = visibleSections.find((s) => s.section.uri === sectionUri);
    if (section) {
      onSelect?.({
        ...section.section,
        validateOnDisplay: true,
      });
    }
  };

  const fieldErrors = missingRequired
    .filter((field) => !field.groupUri)
    .map((field) => ({
      name: field.uri,
      label: field.label,
      error: requiredMessage,
      order: field.order,
      sectionIndex: visibleSections.findIndex(
        (s) => s.section.uri === field.sectionUri,
      ),
      onClick: () => onGotoError(field.sectionUri),
    }));

  const groupErrors = visibleSections
    .flatMap((section) => section.orderedItems)
    .filter((orderedItem) => orderedItem.type === "group")
    .map((orderedItem) => {
      const orderedGroup = orderedItem as unknown as OrderedGroup;

      return orderedGroup.groupFieldSets.map((fieldSet) => {
        const index = fieldSet.index || 0;
        const groupFieldErrors = fieldSet.visibleFields
          .map((field) =>
            field.mandatory &&
            !checkHasResponse(field.type, responses[field.uri]?.[index])
              ? {
                  name: `${field.uri}-${index}`,
                  label: field.label,
                  error: requiredMessage,
                  onClick: () => onGotoError(field.sectionUri),
                }
              : undefined,
          )
          .filter(isNotNullOrUndefined);

        return groupFieldErrors.length
          ? {
              name: orderedGroup.group.repeatable
                ? `${orderedGroup.group.uri}-${index}`
                : orderedGroup.group.uri,
              index: orderedGroup.group.repeatable ? index : undefined,
              label: orderedGroup.group.label,
              order: orderedGroup.fields[0].order,
              sectionIndex: visibleSections.findIndex(
                (s) => s.section.uri === orderedGroup.group.sectionUri,
              ),
              fieldErrors: groupFieldErrors,
            }
          : undefined;
      });
    })
    .flat()
    .filter(isNotNullOrUndefined);

  return (
    <PageWrapper sidebar={sidebar}>
      <Page
        isPreview={isPreview}
        className={previewErrorToggle && "mb-[5.6rem]"}
      >
        {isLoadingDBResponses && <Loading size="large" color="primary" />}
        <Section>
          <SectionForm onSubmit={onSubmit} onReset={onReset} isForm={isForm}>
            <SectionButtons
              isForm={isForm}
              team={team}
              hasError={submitError}
              hasPayment={hasPayment}
              setSidebar={setSidebar}
              onPrevious={
                onSelect
                  ? () =>
                      onSelect(
                        visibleSections[visibleSections.length - 1].section,
                      )
                  : undefined
              }
              onSubmit={onSubmitButton || noop}
              disabled={{
                submit:
                  (!previewErrorToggle?.[0] && !!missingRequired.length) ||
                  autoFillingStatus === "filling",
              }}
              showSectionsButton
            >
              <header className="flex flex-col gap-small">
                <h1 className="font-semibold text-h1 font-poppins">
                  {summaryTitle}
                </h1>
                <p className="text-paragraph">Please review your responses</p>
              </header>
              {!previewErrorToggle?.[0] && !!missingRequired.length && (
                <ErrorSummary
                  errors={[...fieldErrors, ...groupErrors].sort((a, b) =>
                    a.sectionIndex === b.sectionIndex
                      ? a.order.localeCompare(b.order)
                      : a.sectionIndex - b.sectionIndex,
                  )}
                />
              )}
              <div className="mt-[5.6rem]">
                {visibleSections.map(({ orderedItems, section }) => (
                  <SummarySection
                    key={section.uri}
                    section={section}
                    onEdit={onSelect ? () => onSelect(section) : undefined}
                  >
                    {renderOrderedItems({
                      orderedItems,
                      responses,
                      renderGroup: (props) => <SummaryGroup {...props} />,
                      renderField: (props) => <SummaryField {...props} />,
                      renderGroupFieldSet: (props) => (
                        <SummaryGroupFieldSet {...props} />
                      ),
                    })}
                  </SummarySection>
                ))}
                <div className="border-[0.1rem] border-neutral-100 mb-[5.6rem]" />
              </div>
            </SectionButtons>
          </SectionForm>
        </Section>
      </Page>
      {previewErrorToggle && (
        <PreviewFormFooter previewErrorToggle={previewErrorToggle} />
      )}
    </PageWrapper>
  );
};
